<script lang="ts">
	import {
		thermostatsMainFilter,
		thermostatsFilter,
		resetThermostatsMainFilter,
		getSelectedThermostatFilterCount,
	} from 'src/stores/ui/filters';
	import { onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import {
		Toolbar,
		Breadcrumbs,
		IconButton,
		Input,
		Chip,
		Button,
		BreadcrumbsFlexWrapper,
	} from '@Shelly/shelly-design-system';
	import ThermostatsFilterModal from './ThermostatsFilterModal.svelte';
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	import { edit } from 'src/stores/ui/edit_lists';
	import { defaultSelectedRoom, defaultThermostatFilter } from 'src/helpers/constants';
	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let type = 'thermostats';
	// Retained for parity with the caller's Toolbar API but not consumed here.
	// `RoomToolbarWrapper` handles the room-scoped thermostats path directly.
	export const id: number | string | undefined = undefined;
	export let disableReorder = false;

	const headerHandle = createListHeaderHandle();

	let openFilterModal = false;

	$: filterCount = getSelectedThermostatFilterCount($thermostatsFilter);
	$: notification =
		filterCount > 0 ? ({ title: filterCount, type: ComponentType.DANGER } as const) : null;

	function resetData() {
		resetThermostatsMainFilter();
		thermostatsFilter.set({ rooms: [defaultSelectedRoom], type: [defaultThermostatFilter] });
	}

	function resetRoomsData() {
		resetThermostatsMainFilter();
		thermostatsFilter.set({ ...$thermostatsFilter, rooms: [defaultSelectedRoom] });
	}

	function resetTypeData() {
		resetThermostatsMainFilter();
		thermostatsFilter.set({ ...$thermostatsFilter, type: [defaultThermostatFilter] });
	}

	$: if ($showNewLayout && type === 'thermostats') {
		const hideInlineSearch = $is_mobile || $is_tablet;
		const config: ListHeaderConfig = $edit
			? {
					primaryAction: {
						label: $format('label_finish_reordering', { default: 'Finish reordering' }),
						icon: 'fas fa-xmark',
						variant: 'primary',
						onClick: () => edit.set(false),
					},
				}
			: {
					menuItems: [
						{
							items: [
								{
									title: $format('label_reorder_items', { default: 'Reorder items' }),
									icon: 'fa-solid fa-arrows-up-down-left-right',
									disabled: disableReorder,
									onClick: () => edit.set(true),
								},
							],
						},
					],
					search: hideInlineSearch
						? {
								value: $thermostatsMainFilter,
								label: capFirstChar($format('search', { default: 'Search' })),
								id: 'thermostats-kebab-search-input',
								onInput: (value) => thermostatsMainFilter.set(value),
								filter: {
									onClick: () => {
										openFilterModal = true;
									},
									ariaLabel: $format('aria_open_filter', { default: 'Open filter' }),
									notification,
								},
							}
						: null,
				};
		headerHandle.set(config);
	}

	onDestroy(() => {
		if (type === 'thermostats') {
			headerHandle.clear();
		}
	});
</script>

{#if $showNewLayout && type === 'thermostats'}
	{#if !$edit}
		{@const hideInlineSearch = $is_mobile || $is_tablet}
		{#if !hideInlineSearch || notification}
			<Toolbar
				class="mt-m mb-m"
				isSticky
				useNewLayout
				noBreadcrumbs
				hideSlotLeftSide={hideInlineSearch}
				hideSlotRightSide
				hideSlotBottom={!notification}
			>
				<svelte:fragment slot="leftSide">
					{#if !hideInlineSearch}
						<div class="d-flex align-items-center justify-content-center">
							<Input
								class="width-md-l mr-sm-s text-capitalize-placeholder"
								bind:value={$thermostatsMainFilter}
								type="text"
								label={capFirstChar($format('search', { default: 'Search' }))}
								icon="fas fa-search"
								id="thermostats-toolbar-search-input"
								aria-labelledby="thermostats-toolbar-search-input"
							/>
							<IconButton
								icon="fa-solid fa-filter"
								on:click={() => {
									openFilterModal = true;
								}}
								aria-label={$format('aria_open_filter', { default: 'Open filter' })}
								{notification}
							/>
						</div>
					{/if}
				</svelte:fragment>

				<svelte:fragment slot="bottom">
					{#if notification}
						<div class="d-flex align-items-center">
							{#each $thermostatsFilter.rooms as room}
								{#if room.value !== defaultSelectedRoom.value}
									<Chip
										aria-label={room.title}
										showChip
										title={room.title}
										class="mr-s flex-shrink-0"
										on:close={resetRoomsData}
									/>
								{/if}
							{/each}
							{#each $thermostatsFilter.type as t}
								{#if t.value !== defaultThermostatFilter.value}
									<Chip
										aria-label={t.title}
										showChip
										title={t.title}
										class="mr-s flex-shrink-0"
										on:close={resetTypeData}
									/>
								{/if}
							{/each}
							<IconButton
								on:click={resetData}
								icon="fas fa-xmark"
								variant="secondary"
								class="width-full"
							>
								{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
							</IconButton>
						</div>
					{/if}
				</svelte:fragment>
			</Toolbar>
		{/if}
	{/if}
{:else}
	<Toolbar
		class="mb-m"
		isSticky
		useNewLayout={$showNewLayout}
		hideSlotLeftSide={$edit || (!$is_mobile && type !== 'room')}
		hideSlotRightSide={($is_mobile && type !== 'room') ||
			$thermostatsFilter.rooms[0].value === defaultSelectedRoom.value}
		hideSlotBottom={!notification || $edit || ($is_mobile && type !== 'room')}
	>
		<svelte:fragment slot="breadcrumbs">
			{#if $is_mobile && type !== 'room'}
				<BreadcrumbsFlexWrapper
					hideSlotRight={$thermostatsFilter.rooms[0].value === defaultSelectedRoom.value}
					gap="s"
				>
					<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
						<Breadcrumbs
							listClasses="width-full"
							aria-label={$format('label_thermostats_data_card', {
								default: 'Thermostats',
							})}
							links={[{ label: capFirstChar($format('label_thermostats_data_card')) }]}
						/>
					</div>

					<Button
						slot="right"
						size="m"
						variant="secondary"
						class="align-self-center"
						icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
						disabled={disableReorder}
						on:click={() => {
							edit.set(!$edit);
						}}
					>
						{$edit
							? $format('label_finish_reordering', { default: 'Finish reordering' })
							: $format('label_reorder_items', { default: 'Reorder items' })}
					</Button>
				</BreadcrumbsFlexWrapper>
			{:else if !$is_mobile && type !== 'room'}
				<BreadcrumbsFlexWrapper
					gap="s"
					hideSlotRight={$thermostatsFilter.rooms[0].value === defaultSelectedRoom.value}
				>
					<div
						class="d-flex align-items-center flex-1 min-width-0 max-width-fit-content"
						slot="left"
					>
						<Breadcrumbs
							class="flex-1 min-width-0 mr-s"
							listClasses="width-full"
							aria-label={$format('label_thermostats_data_card', {
								default: 'Thermostats',
							})}
							links={[{ label: capFirstChar($format('label_thermostats_data_card')) }]}
						/>

						{#if !$edit}
							<div class="d-flex align-items-center justify-content-center">
								<Input
									class="width-md-l mr-sm-s text-capitalize-placeholder"
									bind:value={$thermostatsMainFilter}
									type="text"
									label={capFirstChar($format('search', { default: 'Search' }))}
									icon="fas fa-search"
									id="thermostats-toolbar-search-input"
									aria-labelledby="thermostats-toolbar-search-input"
								/>
								<IconButton
									icon="fa-solid fa-filter"
									on:click={() => {
										openFilterModal = true;
									}}
									aria-label={$format('aria_open_filter', { default: 'Open filter' })}
									{notification}
								/>
							</div>
						{/if}
					</div>

					<Button
						slot="right"
						size="m"
						variant="secondary"
						class="align-self-center"
						icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
						disabled={disableReorder}
						on:click={() => {
							edit.set(!$edit);
						}}
					>
						{$edit
							? $format('label_finish_reordering', { default: 'Finish reordering' })
							: $format('label_reorder_items', { default: 'Reorder items' })}
					</Button>
				</BreadcrumbsFlexWrapper>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="leftSide">
			{#if type === 'thermostats'}
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-l mr-sm-s text-capitalize-placeholder"
						bind:value={$thermostatsMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="thermostats-toolbar-search-input"
						aria-labelledby="thermostats-toolbar-search-input"
					/>
					<IconButton
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>
			{/if}
			{#if notification && $is_mobile}
				<div class="d-flex align-items-center">
					<IconButton
						on:click={resetData}
						icon="fas fa-xmark"
						variant="secondary"
						class="width-full"
					>
						{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
					</IconButton>
				</div>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			{#if notification}
				<div class="d-flex align-items-center">
					{#if !$is_mobile}
						{#each $thermostatsFilter.rooms as room}
							{#if room.value !== defaultSelectedRoom.value}
								<Chip
									aria-label={room.title}
									showChip
									title={room.title}
									class="mr-s flex-shrink-0"
									on:close={resetRoomsData}
								/>
							{/if}
						{/each}
						{#each $thermostatsFilter.type as t}
							{#if t.value !== defaultThermostatFilter.value}
								<Chip
									aria-label={t.title}
									showChip
									title={t.title}
									class="mr-s flex-shrink-0"
									on:close={resetTypeData}
								/>
							{/if}
						{/each}
					{/if}
					<IconButton
						on:click={resetData}
						icon="fas fa-xmark"
						variant="secondary"
						class="width-full"
					>
						{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
					</IconButton>
				</div>
			{/if}
		</svelte:fragment>
	</Toolbar>
{/if}

{#if openFilterModal}
	<ThermostatsFilterModal bind:openFilterModal />
{/if}
